import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { imActions, imAsPeerIdSelector } from '$store/im';
import { MessageModel } from '$store/models';

export function useImActions() {
  const dispatch = useDispatch();
  const asPeerId = useSelector(imAsPeerIdSelector);
  const as = useMemo(
    () => (asPeerId ? { asPeerId } : {}),
    [asPeerId],
  );

  const loadDialogs = useCallback(() => {
    dispatch(imActions.loadDialogs(asPeerId ? { asPeerId } : undefined));
  }, [dispatch, asPeerId]);

  const loadMoreDialogs = useCallback(
    (nextFrom: string) => {
      dispatch(imActions.loadMoreDialogs({ nextFrom, ...as }));
    },
    [dispatch, as],
  );

  const sendMessage = useCallback(
    (peerId: number) => {
      dispatch(imActions.sendMessage({ peerId, ...as }));
    },
    [dispatch, as],
  );

  const loadChat = useCallback(
    (peerId: number) => {
      dispatch(imActions.loadChat({ peerId, ...as }));
    },
    [dispatch, as],
  );

  const loadMoreHistory = useCallback(
    (peerId: number, nextFrom: string) => {
      dispatch(imActions.loadMoreHistory({ peerId, nextFrom, ...as }));
    },
    [dispatch, as],
  );

  const readHistory = useCallback(
    (peerId: number) => {
      dispatch(imActions.readHistory({ peerId, ...as }));
    },
    [dispatch, as],
  );

  const deleteDialog = useCallback(
    (peerId: number) => {
      dispatch(imActions.deleteDialog({ peerId, ...as }));
    },
    [dispatch, as],
  );

  const deleteMessage = useCallback(
    (id: number, peerId: number) => {
      dispatch(imActions.deleteMessage({ id, peerId, ...as }));
    },
    [dispatch, as],
  );

  const deleteUnsentMessage = useCallback(
    (id: number, peerId: number) => {
      dispatch(imActions.deleteUnsentMessage({ id, peerId, ...as }));
    },
    [dispatch, as],
  );

  const sendTyping = useCallback(
    (peerId: number) => {
      dispatch(imActions.sendTyping({ peerId, ...as }));
    },
    [dispatch, as],
  );

  const retrySend = useCallback(
    (peerId: number, message: MessageModel) => {
      dispatch(imActions.retrySend({ peerId, message, ...as }));
    },
    [dispatch, as],
  );

  const loadWrite = useCallback(() => {
    dispatch(imActions.loadWrite(asPeerId ? { asPeerId } : undefined));
  }, [dispatch, asPeerId]);

  return {
    loadDialogs,
    loadMoreDialogs,
    sendMessage,
    loadChat,
    loadMoreHistory,
    readHistory,
    deleteDialog,
    deleteMessage,
    deleteUnsentMessage,
    sendTyping,
    retrySend,
    loadWrite,
  };
}
